import type { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import {
  BreadcrumbJsonLd,
  FAQPageJsonLd,
  LocalBusinessJsonLd,
  LogoJsonLd,
  NextSeo,
  OrganizationJsonLd,
  WebPageJsonLd
} from 'next-seo'
import dynamic from 'next/dynamic'
import React from 'react'
import Footer from 'src/components/Footer'
import LandingNav from 'src/components/LandingNav'
import WhatsAppButton from 'src/components/WhatsappButton'
import { DEFAULT_STRUCTURED_DATA } from 'src/constants/structuredData'
import LandingPage from 'src/screens/Landing/LandingPage'
import { toOgLocale } from 'src/utils/seo'

const GooglePopup = dynamic(() => import('src/components/Login/GooglePopup'), {
  ssr: false
})

const SEO_TITLE =
  'Renta de Mini Bodegas, Estacionamientos y Bodegas en México | SpotMe'
const SEO_IMAGE = 'https://www.spotme.mx/images/cover.png'
const SEO_URL = 'https://www.spotme.mx'

type IProps = {
  locale: string
}

const Page: NextPage<IProps> = ({ locale }) => {
  const { t } = useTranslation('landing')
  const { t: tStats } = useTranslation('platform_stats')
  const faqsRaw = t('INDEX_FAQ.ITEMS', {
    returnObjects: true,
    cities: tStats('CITIES')
  })
  const faqs = (Array.isArray(faqsRaw) ? faqsRaw : []) as {
    title: string
    description: string
  }[]
  const seoDescription = `Renta mini bodegas, estacionamientos mensuales, bodegas comerciales y naves industriales en CDMX, Monterrey, Guadalajara y ${tStats('CITIES')} ciudades. Compara precios, reserva en línea y arrienda por meses con contratos flexibles.`

  return (
    <>
      <NextSeo
        title={SEO_TITLE}
        description={seoDescription}
        canonical={SEO_URL}
        openGraph={{
          type: 'website',
          url: SEO_URL,
          title: SEO_TITLE,
          description: seoDescription,
          locale: toOgLocale(locale),
          images: [{ url: SEO_IMAGE, width: 1200, height: 628 }]
        }}
        twitter={{
          handle: '@spotme_mx',
          cardType: 'summary_large_image'
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content:
              'mini bodegas en renta, pension estacionamiento, estacionamiento para flotillas, bodegas comerciales, renta de naves industriales, renta de bodegas, renta de espacios, bodega en renta mensual'
          }
        ]}
      />
      <OrganizationJsonLd
        type="Corporation"
        id={DEFAULT_STRUCTURED_DATA.id}
        logo={DEFAULT_STRUCTURED_DATA.logo}
        legalName={DEFAULT_STRUCTURED_DATA.legalName}
        name={DEFAULT_STRUCTURED_DATA.name}
        address={DEFAULT_STRUCTURED_DATA.address}
        contactPoint={[DEFAULT_STRUCTURED_DATA.contactPoint]}
        sameAs={DEFAULT_STRUCTURED_DATA.sameAs}
        url={DEFAULT_STRUCTURED_DATA.url}
      />
      <LogoJsonLd
        logo={DEFAULT_STRUCTURED_DATA.logo}
        url={DEFAULT_STRUCTURED_DATA.url}
      />
      <LocalBusinessJsonLd
        type="SelfStorage"
        id={SEO_URL + '#localbusiness'}
        name="SpotMe"
        description="Marketplace de renta de mini bodegas, estacionamientos mensuales, bodegas comerciales y naves industriales en México. Compara precios y arrienda por meses."
        url={SEO_URL}
        telephone="+52-81-1821-6319"
        sameAs={DEFAULT_STRUCTURED_DATA.sameAs}
        address={{
          streetAddress: 'Av. Manuel Gómez Morín 350-PB 06A',
          addressLocality: 'San Pedro Garza García',
          addressRegion: 'N.L.',
          postalCode: '66265',
          addressCountry: 'MX'
        }}
        geo={{
          latitude: 25.6514,
          longitude: -100.3491
        }}
        images={[SEO_IMAGE]}
        // No `rating` (2026-09-30): self-serving review markup on our own
        // business is against Google's policy, and the numbers were static
        // copy, not real reviews.
        openingHours={[
          {
            opens: '09:00',
            closes: '18:00',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
          }
        ]}
        areaServed={[
          {
            geoMidpoint: { latitude: 25.68, longitude: -100.31 },
            geoRadius: '50000'
          },
          {
            geoMidpoint: { latitude: 19.4285, longitude: -99.1277 },
            geoRadius: '50000'
          },
          {
            geoMidpoint: { latitude: 20.6668, longitude: -103.3918 },
            geoRadius: '50000'
          },
          {
            geoMidpoint: { latitude: 32.5149, longitude: -117.0382 },
            geoRadius: '40000'
          },
          {
            geoMidpoint: { latitude: 19.0414, longitude: -98.2063 },
            geoRadius: '40000'
          },
          {
            geoMidpoint: { latitude: 20.5888, longitude: -100.3899 },
            geoRadius: '40000'
          },
          {
            geoMidpoint: { latitude: 21.1219, longitude: -101.6802 },
            geoRadius: '40000'
          },
          {
            geoMidpoint: { latitude: 28.6353, longitude: -106.0889 },
            geoRadius: '40000'
          },
          {
            geoMidpoint: { latitude: 20.9674, longitude: -89.5926 },
            geoRadius: '40000'
          },
          {
            geoMidpoint: { latitude: 21.1619, longitude: -86.8515 },
            geoRadius: '40000'
          },
          {
            geoMidpoint: { latitude: 21.8853, longitude: -102.2916 },
            geoRadius: '30000'
          },
          {
            geoMidpoint: { latitude: 29.0729, longitude: -110.9559 },
            geoRadius: '40000'
          },
          {
            geoMidpoint: { latitude: 20.6534, longitude: -105.2253 },
            geoRadius: '30000'
          },
          {
            geoMidpoint: { latitude: 19.2826, longitude: -99.6557 },
            geoRadius: '30000'
          }
        ]}
      />
      <WebPageJsonLd
        description={seoDescription}
        id={SEO_URL + '#webpage'}
        lastReviewed={new Date().toISOString().split('T')[0]}
        reviewedBy={{ '@type': 'Organization', name: 'SpotMe' }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Inicio', item: 'https://www.spotme.mx/' }
        ]}
      />
      <FAQPageJsonLd
        mainEntity={faqs.map(
          ({ title, description }: { title: string; description: string }) => ({
            questionName: title,
            acceptedAnswerText: description
          })
        )}
      />
      <main>
        <LandingNav />
        <LandingPage />
        <Footer />

        <WhatsAppButton />
        <GooglePopup />
      </main>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translations = await serverSideTranslations(locale ?? 'es', [
    'common',
    'landing',
    'footer',
    'nav',
    'platform_stats',
    'auth'
  ])

  return { props: { ...translations, locale } }
}

export default React.memo(Page)
